import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Search, Phone, Send, User } from "lucide-react";

const ConversationsPage = () => {
  const { API, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedPhone) {
      fetchMessages(selectedPhone);
    }
  }, [selectedPhone]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/conversations`, getAuthHeader());
      setConversations(response.data);
      if (response.data.length > 0 && !selectedPhone) {
        setSelectedPhone(response.data[0].phone);
      }
    } catch (error) {
      console.error("Fetch conversations error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (phone) => {
    try {
      const response = await axios.get(`${API}/whatsapp/messages?phone=${phone}`, getAuthHeader());
      setMessages(response.data.reverse());
    } catch (error) {
      console.error("Fetch messages error:", error);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  };

  const filteredConversations = conversations.filter((conv) => {
    const search = searchTerm.toLowerCase();
    return (
      conv.phone?.toLowerCase().includes(search) ||
      conv.customer?.name?.toLowerCase().includes(search)
    );
  });

  const selectedConversation = conversations.find((c) => c.phone === selectedPhone);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="conversations-title">
          WhatsApp Conversations
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          View and manage customer chat history
        </p>
      </div>

      {/* Chat Interface */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-220px)]">
        {/* Conversations List */}
        <Card className="border-zinc-200 shadow-none lg:col-span-1">
          <CardHeader className="pb-2 border-b border-zinc-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9 rounded-sm"
                data-testid="conversation-search"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-330px)]">
              {loading ? (
                <div className="p-4 text-center text-zinc-500">Loading...</div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-8 text-center text-zinc-400">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                  <p>No conversations yet</p>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <div
                    key={conv.phone}
                    onClick={() => setSelectedPhone(conv.phone)}
                    className={`flex items-center gap-3 p-4 cursor-pointer border-b border-zinc-100 transition-colors ${
                      selectedPhone === conv.phone ? "bg-zinc-100" : "hover:bg-zinc-50"
                    }`}
                    data-testid={`conversation-${conv.phone}`}
                  >
                    <div className="w-10 h-10 bg-[#0A192F] flex items-center justify-center text-white text-sm font-medium">
                      {conv.customer?.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-zinc-900 truncate">
                          {conv.customer?.name || conv.phone}
                        </p>
                        <span className="text-xs text-zinc-400">
                          {formatDate(conv.last_timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-500 truncate">
                        {conv.last_message}
                      </p>
                    </div>
                    {conv.message_count > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {conv.message_count}
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat Window */}
        <Card className="border-zinc-200 shadow-none lg:col-span-2 flex flex-col">
          {selectedPhone ? (
            <>
              {/* Chat Header */}
              <CardHeader className="pb-3 border-b border-zinc-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A192F] flex items-center justify-center text-white text-sm font-medium">
                      {selectedConversation?.customer?.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="font-medium text-zinc-900">
                        {selectedConversation?.customer?.name || selectedPhone}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-zinc-500">
                        <Phone className="w-3 h-3" />
                        {selectedPhone}
                      </div>
                    </div>
                  </div>
                  {selectedConversation?.customer?.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/customers/${selectedConversation.customer.id}`)}
                      data-testid="view-customer-profile"
                    >
                      <User className="w-4 h-4 mr-2" />
                      View Profile
                    </Button>
                  )}
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-0 overflow-hidden">
                <ScrollArea className="h-[calc(100vh-400px)] p-4">
                  <div className="space-y-3">
                    {messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-48 text-zinc-400">
                        <MessageSquare className="w-8 h-8 mb-2" />
                        <p>No messages in this conversation</p>
                      </div>
                    ) : (
                      messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[70%] px-4 py-2 ${
                              msg.direction === "outbound"
                                ? "chat-bubble-outbound"
                                : "chat-bubble-inbound"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${msg.direction === "outbound" ? "text-white/60" : "text-zinc-400"}`}>
                              {formatTime(msg.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Message Input (Read-only indicator) */}
              <div className="p-4 border-t border-zinc-100">
                <div className="flex items-center gap-2 text-sm text-zinc-500 bg-zinc-50 p-3">
                  <MessageSquare className="w-4 h-4" />
                  <span>Messages are received via WhatsApp webhook. This is a read-only view.</span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
              <MessageSquare className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm">Choose a customer from the list to view their chat history</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ConversationsPage;
